<template>
  <div
    v-if="isVisible"
    class="a-modal"
    :class="classes"
    @click="hide"
  >
    <div
      class="modal-inner"
      @click="stopEvent"
    >
      <div class="modal-header">
        <h2
          v-if="title"
          v-text="title"
          :title="title"
        />
        <div
          v-if="enableClose"
          class="close-button"
          @click="hide"
        >
          <CloseIcon />
        </div>
      </div>
      <div class="modal-body">
        <slot />
      </div>
      <div
        v-if="hasActions"
        class="modal-actions"
      >
        <slot
          v-if="hasActionsInSlot"
          name="actions"
        />
        <template v-else-if="actions.length > 0">
          <div
            class="action"
            v-for="(action, index) in actions"
            :key="index"
            :class="getClasses(action)"
            @click="(event) => callHandler(event, action)"
            :title="action.label"
          >
            {{ action.label }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import CloseIcon from './CloseIcon'

export default {
  name: 'AModal',
  components: {
    CloseIcon
  },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    actions: {
      type: Array,
      required: false,
      default: () => ([])
    },
    disablePortaling: {
      type: Boolean,
      required: false,
      default: false
    },
    enableClose: {
      type: Boolean,
      required: false,
      default: true
    },
    size: {
      type: String,
      required: false,
      default: 'md'
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    classes() {
      const classes = []
      classes.push(this.size)
      return classes
    },
    hasActionsInSlot() {
      return this.$slots.actions && this.$slots.actions.length > 0
    },
    hasActions() {
      return this.actions.length > 0 || this.hasActionsInSlot
    }
  },
  methods: {
    hide(event) {
      this.stopEvent(event)
      this.isVisible = false
      /**
       * TODO: Fix inner modal position to allow z-index context
       * to apply for the product card modal, currently because the modal is inside the product card
       * the fixed checkout bar will always sit above it. Z-index has no effect
       * on elements in the dom within inner lays. This emission is
       * to toggle the z-index on a Stepper component.
       */
      this.$emit('modalhidden')
    },
    stopEvent(event) {
      event.stopPropagation()
    },
    callHandler(event, action) {
      event.stopPropagation()
      if (action.handler && typeof action.handler === 'function') {
        action.handler();
      }
    },
    getClasses(action) {
      if (action.classes) {
        return action.classes
      }
      return {}
    }
  }
}
</script>

<style lang="scss">
@import '~@scss/custom.scss';

:root {
  --modal-margin: 1em;
  --modal-padding: 1em;
  --modal-background: #ffffff;
  --modal-box-shadow: 1px 1px black;
  --modal-backdrop-background: rgba(0, 0, 0, 0.8);
  --modal-close-button-size: 1.75em;
  --modal-border-radius:3px;
  --modal-close-icon-color:var(--main-link-color);
  --modal-close-icon-hover-color:var(--main-link-hover-color);
}

// reset bootstrap styling
.a-modal {
  .modal-header {
    display: block;
    padding: 0;
    border-bottom: none;
    border-radius: 0;
  }
  .modal-body {
    display: block;
    padding: 0;
  }
}

</style>

<style lang="scss" scoped>
@import '~@scss/custom.scss';

.a-modal {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  &::after {
    content: '';
    width: 100%;
    height: 100%;
    background: var(--modal-backdrop-background);
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
  }
  .modal-inner {
    z-index: 1001;
    background: #ffffff;
    max-width: calc(100% - var(--modal-margin));
    max-height: calc(100% - var(--modal-margin));
    width: get-breakpoints("md");
    background: var(--modal-background);
    margin: var(--modal-margin);
    border-radius: var(--modal-border-radius);
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    box-shadow: var(--modal-box-shadow);
    .modal-header {
      padding: var(--modal-padding);
      display: flex;
      justify-content: flex-start;
      align-items: center;
      h2 {
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-right: 2em;
      }
      .close-button {
        margin-left: auto;
        flex-shrink: 0;
        flex-grow: 0;
        flex-basis: initial;
        width: var(--modal-close-button-size);
        height: var(--modal-close-button-size);
        cursor: pointer;
        ::v-deep svg {
          path {
            transition: fill 0.3s;
            fill: var(--modal-close-icon-color);
          }
        }
        &:hover {
          ::v-deep svg {
            path {
              fill: #ff0000;
            }
          }
        }
      }
    }
    .modal-body {
      padding: var(--modal-padding);
    }
    .modal-actions {
      display: flex;
      flex-direction: row;
      justify-content: center;
      border-top: 1px solid rgba(0, 0, 0, 0.1);
      ::v-deep .action {
        padding: 1.25rem 0.75rem;
        flex-grow: 1;
        flex-basis: initial;
        display: flex;
        justify-content: center;
        cursor: pointer;
        background-color: #f9f9f9;
        &:hover, &:focus {
          background-color: #e7e7e7;
        }
        &.disabled {
          cursor: not-allowed;
          background-color: #d2d2d2;
        }
      }
      @include media-breakpoint-up(md) {
        ::v-deep .action {
          &:not(:last-child) {
            border-right: 1px solid rgba(0, 0, 0, 0.1);
          }
        }
      }
      @include media-breakpoint-down(md) {
        flex-direction: column;
        ::v-deep .action {
          &:not(:last-child) {
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          }
        }
      }
    }
  }
}

.a-modal {
  &.xs {
    .modal-inner {
      width: get-breakpoints("sm");
    }
  }
  &.sm {
    .modal-inner {
      width: get-breakpoints("sm");
    }
  }
  &.md {
    .modal-inner {
      width: get-breakpoints("md");
    }
  }
  &.lg {
    .modal-inner {
      width: get-breakpoints("lg");
    }
  }
  &.xl {
    .modal-inner {
      width: get-breakpoints("xl");
    }
  }
}
</style>
